<template>
    <div>
        <div class="shadow_box rounded-lg p-6 relative flex flex-col">
            <p class="title"><strong>Pesquisa</strong></p>
            <form class="mt-1 w-full" @submit.prevent="reloadTable">
                <div class="flex flex-wrap mb-4 px-4">
                    <multi-select-input
                        v-model="filters.WeekReport.sales_promoter"
                        :multiple="true"
                        :options="options.salesPromoterSearch"
                        class="lg:pr-6 w-full lg:w-1/4"
                        label="Promotor"
                    />

                    <multi-select-input
                        v-model="filters.WeekReport.market_network"
                        :multiple="true"
                        :options="options.marketNetworkSearch"
                        class="lg:pr-6 w-full lg:w-1/4"
                        label="Rede"
                    />

                    <multi-select-input
                        v-model="filters.WeekReport.market"
                        :multiple="true"
                        :options="options.marketSearch"
                        class="lg:pr-6 w-full lg:w-1/4"
                        label="Mercado"
                    />

                    <multi-select-input
                        v-model="filters.WeekReport.provider"
                        :multiple="true"
                        :options="options.providerSearch"
                        class="lg:pr-6 w-full lg:w-1/4"
                        label="Fornecedor"
                    />

                    <multi-select-input
                        v-model="filters.WeekReport.brand"
                        :multiple="true"
                        :options="options.brandSearch"
                        class="lg:pr-6 w-full lg:w-1/4"
                        label="Marca"
                    />
                </div>
                <div class="flex flex-shrink-0 flex-wrap items-center justify-start p-4 rounded-b-md gap-1">
                    <button
                        class="btn-purple px-6 py-2.5 text-white font-roboto font-medium text-xs leading-tight uppercase rounded shadow-md hover:bg-gray-700 hover:shadow-lg focus:bg-gray-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-gray-800 active:shadow-lg transition duration-150 ease-in-out">
                        <font-awesome-icon icon="fa-magnifying-glass" class="text-white text-sm mr-0 lg:mr-2" />
                        Pesquisar
                    </button>

                    <button class="btn flex items-center text-purple-100 font-roboto font-medium px-6 py-2.5 text-xs leading-tight uppercase border-solid border-2 border-purple-100" @click="clearSearch">
                        <font-awesome-icon icon="fa-eraser" class="text-purple text-sm mr-0 lg:mr-2" />
                        Limpar
                    </button>
                </div>
            </form>
        </div>


        <Head title="Relatório Semanal" />
        <h1 class="page-title">Relatório Semanal</h1>


        <div class="report-table-wrapper mt-4">
            <div class="month-pagination-wrapper">
                <font-awesome-icon icon="fa-solid fa-chevron-left" class="cursor-pointer" @click="changeDate(-7)" />
                <p class="text-base sm:text-xl">
                    {{ dateStart }} - {{ dateEnd }}
                </p>
                <font-awesome-icon icon="fa-solid fa-chevron-right" class="cursor-pointer" @click="changeDate(7)" />
            </div>
            <div class="table-wrapper">
                <div class="text-right w-full">
                    <button 
                        class="inline-block text-center w-[150px] py-3 px-4 btn-purple"
                        @click="handlerExportXlsx">
                            <font-awesome-icon 
                                icon="fa-table-cells" 
                                class="text-purple text-sm mr-0 lg:mr-2" />
        
                            Exportar xlsx
                    </button>
                </div>
                <data-table 
                    v-if="loadTable" 
                    v-model:columns="columns" 
                    route="week_report/list"
                    :query="getQuery" 
                    no-data-label="Nenhuma atividade encontrada"
                    no-result-label="Nenhuma atividade encontrada">

                        <template #table.cell.content.sales_promoter_label="{ value, row }">
                            <Link 
                                href="/week_details" 
                                :data="{ id: row.sales_promoter_id, date: getQueryDate }" 
                                class="text-purple-200 underline">
                                    {{ row.sales_promoter }}
                            </Link>
                        </template>

                        <template #table.cell.content.market="{ value, row }">
                            <tooltip-info :title="row.market" :content="row.marketDetails" />
                        </template>

                        <template #grid.content.body.market="{ value, row }">
                            <tooltip-info :title="row.market" :content="row.marketDetails" />
                        </template>

                        <template #table.cell.content.sunday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.sunday)" 
                                :class="getClassIcon(row.sunday)"
                                size="1x" />
                        </template>

                        <template #grid.content.body.sunday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.sunday)" 
                                :class="getClassIcon(row.sunday)" />
                        </template>

                        <template #table.cell.content.monday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.monday)" 
                                :class="getClassIcon(row.monday)" />
                        </template>

                        <template #grid.content.body.monday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.monday)" 
                                :class="getClassIcon(row.monday)" />
                        </template>

                        <template #table.cell.content.tuesday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.tuesday)"
                                :class="getClassIcon(row.tuesday)" />
                        </template>

                        <template #grid.content.body.tuesday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.tuesday)"
                                :class="getClassIcon(row.tuesday)" />
                        </template>

                        <template #table.cell.content.wednesday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.wednesday)"
                                :class="getClassIcon(row.wednesday)" />
                        </template>

                        <template #grid.content.body.wednesday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.wednesday)"
                                :class="getClassIcon(row.wednesday)" />
                        </template>

                        <template #table.cell.content.thursday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.thursday)"
                                :class="getClassIcon(row.thursday)" />
                        </template>

                        <template #grid.content.body.thursday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.thursday)"
                                :class="getClassIcon(row.thursday)" />
                        </template>

                        <template #table.cell.content.friday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.friday)" 
                                :class="getClassIcon(row.friday)" />
                        </template>

                        <template #grid.content.body.friday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.friday)" 
                                :class="getClassIcon(row.friday)" />
                        </template>
                        
                        <template #table.cell.content.saturday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.saturday)"
                                :class="getClassIcon(row.saturday)" />
                        </template>
                        
                        <template #grid.content.body.saturday="{ value, row }">
                            <font-awesome-icon 
                                :icon="'fa-regular ' + getIcon(row.saturday)"
                                :class="getClassIcon(row.saturday)" />
                        </template>
                </data-table>
            </div>
        </div>
    </div>
    <Modal
        size="modal-lg"
        ref="showModalXlsx">
            <template #header>
                <div class="text-gray-777 font-roboto uppercase w-full">
                    Historico de arquivos xlsx exportados
                </div>
            </template>

            <template #body>
                <p>Deseja exportar um novo arquivo? </p>
                <button 
                    class="inline-block text-center w-[150px] py-3 px-4 btn-purple mt-4"
                    @click="handlerSendToExportData">
                        <font-awesome-icon 
                            icon="fa-table-cells" 
                            class="text-purple text-sm mr-0 lg:mr-2" />

                        Exportar 
                </button>

                <data-table
                    v-if="exportedTable"
                    v-model:columns="columnsExported" 
                    route="week_report/export"
                    no-data-label="Nenhum registro encontrado"
                    no-result-label="Nenhum registro encontrado"
                    class="data-table">

                    <template #table.cell.content.actions="{ value, row }">
                        <button 
                            @click="downloadExportedFile(row.id)" 
                            class="bg-purple-200 text-white rounded p-2 cursor-pointer text-sm mr-4">
                                <font-awesome-icon icon="fa-download" class="text-white px-2" />
                        </button>

                        <button 
                            @click="deleteExportedFile(row.id)"
                            class="bg-red_delete text-white rounded p-2 cursor-pointer text-sm">
                                <font-awesome-icon icon="fa-trash-can" class="text-white px-2" />
                        </button>
                    </template>
                </data-table>
            </template>
            <template #footer>
                <button type="button"
                    class="px-6 py-2.5 bg-gray-600 text-white font-roboto font-medium text-xs leading-tight uppercase rounded shadow-md hover:bg-gray-700 hover:shadow-lg focus:bg-gray-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-gray-800 active:shadow-lg transition duration-150 ease-in-out"
                    @click.prevent="closeModalExport">Cancelar</button>
                
            </template>
    </Modal>
</template>

<script>
import { Head, Link, useForm } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import DataTable from '@/Shared/DataTable'
import SelectInput from '@/Shared/SelectInput'
import TextInput from '@/Shared/TextInput'
import TooltipInfo from '@/Shared/TooltipInfo'
import MultiSelectInput from '@/Shared/MultiSelectInput'

import { useMemorizeFilters } from '@/Mixins/MemorizeFilters'
import { getFiltersMixin } from '@/Mixins/GetFilters'
import Modal from '@/Shared/Modal'
import axios from 'axios'


export default {
  components: {
    Head,
    DataTable,
    SelectInput,
    TextInput,
    TooltipInfo,
    MultiSelectInput,
    Link,
    Modal,
  },
  layout: Layout,
  mixins: [
    getFiltersMixin,
  ],
  data () {
    return {
      loadTable: true,

      date: null,
      exportedTable: true,
      columnsExported: [
        {
          uniqid: 'date',
          label: 'Gerado',
          field: 'created_at',
          visible: true,
          sortable: false,
          sortOrder: 'desc',
          classes: 'px-4 py-3 border-b text-sm col-name',
          headerClass: 'p-4 text-left',
          format: (val, row) => {
            const createdAt = new Date(val)
            return createdAt.toLocaleString('pt-br');
          },
        },
        {
          uniqid: 'title',
          label: 'Nome',
          field: 'title',
          visible: true,
          sortable: false,
          sortOrder: 'desc',
          classes: 'px-4 py-3 border-b text-sm col-name',
          headerClass: 'p-4 text-left',
        },

        {
          uniqid: 'status',
          label: 'Status',
          field: 'status',
          visible: true,
          sortable: false,
          sortOrder: 'desc',
          classes: 'px-4 py-3 border-b text-sm col-name',
          headerClass: 'p-4 text-left',
          format: (val, row) => {
            const status = {
              finish: 'Sucesso',
              failed: 'Falhou',
              processing: 'Processando',
            }

            return status[val]
          }
        },

        {
          uniqid: 'size',
          label: 'Tamanho',
          field: 'size',
          visible: true,
          sortable: false,
          sortOrder: 'desc',
          classes: 'px-4 py-3 border-b text-sm col-name',
          headerClass: 'p-4 text-left',
          format: (val, row) => {
            if (row.file)
              return `${ (row.file.size / (1024 * 1024)).toFixed(2) }MB`
          }
        },

        {
          uniqid: 'actions',
          label: 'Ações',
          field: 'actions',
          visible: true,
          sortable: false,
          sortOrder: 'desc',
          classes: 'px-4 py-3 border-b text-sm col-name',
          headerClass: 'p-4 text-left',
        },
      ],

      options: {
        salesPromotersOptions: [],
        marketNetworkOptions: [],
        marketOptions: [],
        providerOptions: [],
        brandsOpetions: [],
      },

      columns: [
        {
          uniqid: 'sales_promoter_label',
          label: 'PROMOTOR',
          field: 'sales_promoter',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
        },
        {
          uniqid: 'market',
          label: 'MERCADO',
          field: 'market',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
        },
        {
          uniqid: 'provider',
          label: 'FORNECEDOR',
          field: 'provider',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
        },
        {
          uniqid: 'brand',
          label: 'MARCA',
          field: 'brand',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
        },
        {
          uniqid: 'sunday',
          label: 'dom',
          field: 'sunday',
          visible: true,
          sortable: false,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
        },
        {
          uniqid: 'monday',
          label: 'Seg',
          field: 'monday',
          visible: true,
          sortable: false,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
        },
        {
          uniqid: 'tuesday',
          label: 'ter',
          field: 'tuesday',
          visible: true,
          sortable: false,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
        },
        {
          uniqid: 'wednesday',
          label: 'qua',
          field: 'wednesday',
          visible: true,
          sortable: false,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
        },
        {
          uniqid: 'thursday',
          label: 'qui',
          field: 'thursday',
          visible: true,
          sortable: false,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
        },
        {
          uniqid: 'friday',
          label: 'sex',
          field: 'friday',
          visible: true,
          sortable: false,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
        },
        {
          uniqid: 'saturday',
          label: 'Sáb',
          field: 'saturday',
          visible: true,
          sortable: false,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
        },
      ],
    }
  },

  watch: {
    'filters.WeekReport': {
      handler() {
        this.getFiltersFromAPI()
      },
      deep: true,
    },
  },

  created() {
    const dateFromFilter = this.getFilters().week_start


    if (dateFromFilter)
      this.date = new Date(this.$filters.formatDate(dateFromFilter, 'yyyy-MM-dd'))

    else
      this.date = new Date()
        
    this.setDateOnFilters()
  },

  computed: {
    getQuery() {
      return Object.entries( this.getFilters() ).map( ( [key, val] ) => `search[${key}]=${val}` ).join( '&' )
    },

    getQueryDate() {
      return this.$filters.formatDate(this.date, 'yyyy-MM-dd')
    },

    dateStart() {
      return this.$filters.formatDate(this.filters.WeekReport.week_start)
    },

    dateEnd() {
      return this.$filters.formatDate(this.filters.WeekReport.week_end)
    },
  },

  watch: {
    'filters.WeekReport': {
      handler() {
        this.getFiltersFromAPI()
      },
      deep: true,
    }
  },

  methods: {
    changeDate(value) {

      this.date.setDate(this.date.getDate() + value)
            
      const enddate = new Date(this.date.getFullYear(), this.date.getMonth(), this.date.getDate() + 7)

      this.filters.WeekReport.week_start = this.$filters.formatDate(this.date, 'yyyy-MM-dd')
      this.filters.WeekReport.week_end = this.$filters.formatDate(enddate, 'yyyy-MM-dd')

      this.reloadTable();
    },

    setDateOnFilters() {
      const dateend = new Date(this.date.getFullYear(), this.date.getMonth(), this.date.getDate() + 7)

      this.filters.WeekReport.week_start = this.$filters.formatDate(this.date, 'yyyy-MM-dd')
      this.filters.WeekReport.week_end = this.$filters.formatDate(dateend, 'yyyy-MM-dd')
    },

    reloadTable () {
      this.loadTable = false;
      setTimeout(() => this.loadTable = true, 10);
    },
        
    clearSearch () {
      this.date = new Date()

      this.clearFilters();
      this.setDateOnFilters()

      this.reloadTable();
    },
        
    getIcon (status) {
      if (status == 0)
        return 'fa-circle-check';

      else if (status == 1)
        return 'fa-xmark-circle'

      else if (status == 3)
        return 'fa-circle-check'

      return 'fa-circle'
    },

    getClassIcon (status) {
      if (status == 0)
        return 'text-green-600'

      else if (status == 1)
        return 'text-red-700';

      else if (status == 3)
        return 'text-yellow-600';

    },

    reloadExportedTable() {
      this.exportedTable = false;
      setTimeout(() => this.exportedTable = true, 10);
    },
    handlerExportXlsx(value = true) {
      this.$refs.showModalXlsx.showModal = value
    },
    closeModalExport () {
      this.$refs.showModalXlsx.showModal = false;
    },

    async handlerSendToExportData() {
      const filters = this.getFilters()

      const dataStart = new Date(this.date).toISOString()
      const enddate = new Date(this.date.getFullYear(), this.date.getMonth(), this.date.getDate() + 7)
      const dataEnd = new Date(enddate).toISOString()      

      try {

        const response = await axios.post('/week_report/export/xlsx/' + dataStart + '/' + dataEnd, filters)

        if (response.status == 200)
          this.$toast.success(response.data.message)
      }

      catch (err) {
        if (axios.isAxiosError(err))
          this.$toast.error(err.response.data.message)

        else
          this.$toast.error('Erro ao fazer requisição')
      }

      this.handlerExportXlsx(false)
    },


    downloadExportedFile(id) {
      window.open(`/week_report/export/xlsx/download/${id}`)
    },

    async deleteExportedFile(id) {
      try {
        const response = await axios.delete(`/week_report/export/xlsx/${id}`)

        if (response.status == 200)
          this.$toast.success(response.data.message)
      }

      catch (err) {
        if (axios.isAxiosError(err))
          this.$toast.error(err.response.data.message)

        else
          this.$toast.error('Erro ao fazer requisição')
      }

      this.reloadExportedTable()
    },
  },

  setup( props ) {
    const { filtersStore, setFiltersComponent, getFiltersComponent, clearFiltersComponent } = useMemorizeFilters()

    setFiltersComponent( 'WeekReport', {
      sales_promoter: [],
      market_network: [],
      market: [],
      provider: [],
      brand: [],
      product: [],
      week_start: '',
      week_end: '',
    } )

    const clearFilters = () => clearFiltersComponent( 'WeekReport', {
      sales_promoter: [],
      market_network: [],
      market: [],
      provider: [],
      brand: [],
      product: [],
    } )

    const getFilters = () => getFiltersComponent( 'WeekReport' )

    return { 
      getFilters,
      clearFilters, 
      filters: filtersStore.filters
    }
  },
}
</script>


<style scoped>
.page-title {
    font: normal normal normal 32px/38px Roboto;
    color: #828282;
    margin: 1.8rem 0;
}

.report-table-wrapper {
    background: #ffffff;
    border-radius: 6px;
}

.month-pagination-wrapper {
    display: flex;
    padding: 2.5rem 0 1.5rem 0;
    align-items: center;
    gap: 1.75rem;
    justify-content: center;
    color: #A7A7A7;
    font: normal normal normal 1.25rem Poppins
}

.table-wrapper {
    padding: 0 1rem;
}</style>
